/**
 * Delay before the object URL is released.
 *
 * Revoking in the same task as `click()` is the documented way to lose a
 * download: the click only *schedules* the fetch of the blob URL, so tearing
 * the URL down before the browser services it produces a silent no-op — no
 * error, no file, nothing to debug. One macrotask is enough for the fetch to
 * have started, and the blob is freed either way.
 */
const REVOKE_DELAY_MS = 1000;

/**
 * Hands a blob to the browser as a file download.
 *
 * An anchor click rather than `window.open`: the latter is popup-blocked
 * whenever the download follows an awaited call, because by then the code is no
 * longer inside the click handler's synchronous user-gesture window. An
 * anchor-click blob download carries no such requirement — it is not a
 * new-window open at all, just a same-document DOM click.
 */
export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.download = filename;
  link.href = url;
  document.body.append(link);

  try {
    link.click();
  } finally {
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
  }
}
